import { ref, computed, watch } from '@vue/composition-api';
import store from '@/store';

// Debounce utility
const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

export default function useItemsList() {
  // Table columns configuration
  const tableColumns = [
    { key: 'select', label: '', sortable: false, thStyle: { width: '40px' } },
    { key: 'id', sortable: false, label: 'ID', thStyle: { minWidth: '60px' } },
    {
      key: 'itemCode',
      sortable: false,
      label: 'Item Code',
      thStyle: { minWidth: '130px' },
      stickyColumn: true,
    },
    { key: 'itemName', sortable: false, label: 'Item Name', thStyle: { minWidth: '200px' } },
    { key: 'meta_unit', sortable: false, label: 'Unit', thStyle: { minWidth: '80px' } },
    { key: 'meta_itemGroup', sortable: false, label: 'Item Group', thStyle: { minWidth: '150px' } },
    {
      key: 'itemPurchaseRate',
      sortable: false,
      label: 'Purchase Rate',
      thStyle: { minWidth: '120px' },
    },
    { key: 'tradePrice', sortable: false, label: 'Trade Price', thStyle: { minWidth: '120px' } },
    { key: 'retailPrice', sortable: false, label: 'Retail Price', thStyle: { minWidth: '120px' } },
    {
      key: 'salesTaxPercent',
      sortable: false,
      label: 'Sales Tax %',
      thStyle: { minWidth: '100px' },
    },
    {
      key: 'addSalesTaxPercent',
      sortable: false,
      label: 'Add Sales Tax %',
      thStyle: { minWidth: '120px' },
    },
    { key: 'meta_HSCode', sortable: false, label: 'HS Code', thStyle: { minWidth: '100px' } },
    { key: 'isActive', sortable: false, label: 'Status', thStyle: { minWidth: '80px' } },
    { key: 'actions', sortable: false, thStyle: { minWidth: '120px' }, stickyColumn: true },
  ];

  // Pagination and search
  const perPage = ref(10);
  const currentPage = ref(1);
  const totalItems = ref(0);
  const searchQuery = ref('');
  const perPageOptions = [10, 25, 50, 100];

  // Table reference
  const refItemsListTable = ref(null);

  // Loading state
  const isTableLoading = ref(false);

  const selectedRows = ref([]);
  const selectAll = ref(false);
  const fetching = ref(false);

  const businessID = computed(() => store.state.user.businessID);

  let lastRequestTime = 0;

  // Fetch items function
  const fetchItems = async (ctx) => {
    const requestTime = Date.now();
    lastRequestTime = requestTime;

    console.log('fetchItems called with businessID:', businessID.value);

    if (!businessID.value) {
      store.dispatch('app/showToast', {
        title: 'Error',
        text: 'Business ID not found',
        variant: 'danger',
      });
      return [];
    }

    isTableLoading.value = true;
    try {
      console.log('Dispatching fetchItems with params:', {
        businessID: businessID.value,
        page: currentPage.value,
        perPage: perPage.value,
        search: searchQuery.value,
      });

      const data = await store.dispatch('fbr-invoice-items/fetchItems', {
        businessID: businessID.value,
        page: currentPage.value,
        perPage: perPage.value,
        search: searchQuery.value,
      });

      // Avoid updating state if a newer request has been started
      if (requestTime !== lastRequestTime) {
        console.warn('Ignoring stale request results');
        // Return an empty promise or a special value? 
        // Actually, just returning the items from this request is safer 
        // than returning nothing, but ideally we should return null 
        // and have b-table ignore it. BootstrapVue table waits for any result.
        // If we return the items from THIS request, even if stale, 
        // the table will eventually be overwritten by the fresh one.
        return data?.items || [];
      }

      const items = data?.items || [];
      totalItems.value = data?.pagination?.totalCount || 0;

      console.log('Fetched items:', items);
      console.log('Total items:', totalItems.value);

      // Re-apply selection for local items if "Select All" is active
      if (selectAll.value) {
        setTimeout(() => {
          if (refItemsListTable.value && refItemsListTable.value.localItems) {
            const pageEligibleRows = refItemsListTable.value.localItems;
            pageEligibleRows.forEach((row) => {
              const index = refItemsListTable.value.localItems.findIndex((r) => r.id === row.id);
              if (index !== -1) {
                refItemsListTable.value.selectRow(index);
              }
            });
          }
        }, 100);
      }

      return items;
    } catch (error) {
      console.error('Error fetching items:', error);
      store.dispatch('app/showToast', {
        title: 'Error',
        text: error.message || 'Failed to fetch items',
        variant: 'danger',
      });
      return [];
    } finally {
      if (requestTime === lastRequestTime) {
        isTableLoading.value = false;
        fetching.value = false;
      }
    }
  };

  const toggleSelectAll = async () => {
    if (selectAll.value) {
      fetching.value = true;
      try {
        await store.dispatch('fbr-invoice-items/fetchItems', {
          businessID: businessID.value,
          page: 1,
          perPage: 99999, // Get all available data
          search: searchQuery.value,
        });

        const allItems = store.getters['fbr-invoice-items/getItems'] || [];
        selectedRows.value = allItems;

        // Automatically select the UI component rendering rows on the current page
        if (refItemsListTable.value && refItemsListTable.value.localItems) {
          const pageEligibleRows = refItemsListTable.value.localItems;
          pageEligibleRows.forEach((row) => {
            const index = refItemsListTable.value.localItems.findIndex((r) => r.id === row.id);
            if (index !== -1) {
              refItemsListTable.value.selectRow(index);
            }
          });
        }
      } catch (error) {
        console.error('Error fetching all items for Select All:', error);
      } finally {
        fetching.value = false;
      }
    } else {
      selectedRows.value = [];
      if (refItemsListTable.value) {
        refItemsListTable.value.clearSelected();
      }
    }
  };

  // Refetch data
  const refetchData = () => {
    console.log('refetchData called - resetting to page 1 and refreshing table');
    currentPage.value = 1;
    if (refItemsListTable.value) {
      refItemsListTable.value.refresh();
    }
  };

  // Debounced search function
  const debouncedSearch = debounce(() => {
    if (refItemsListTable.value) {
      refItemsListTable.value.refresh();
    }
  }, 500); // 500ms delay

  // Watch for page and perPage changes (immediate)
  watch([currentPage, perPage], () => {
    if (refItemsListTable.value) {
      refItemsListTable.value.refresh();
    }
  });

  // Watch for search query changes (debounced)
  watch(searchQuery, () => {
    if (selectAll.value) {
      selectAll.value = false;
    }
    selectedRows.value = [];
    if (refItemsListTable.value) {
      refItemsListTable.value.clearSelected();
    }
    
    // Reset to first page when search changes
    if (currentPage.value !== 1) {
      currentPage.value = 1;
    } else {
      // If already on page 1, manually trigger debounced search
      debouncedSearch();
    }
  });

  return {
    // Table configuration
    tableColumns,
    refItemsListTable,

    // Loading state
    isTableLoading,

    // Pagination
    perPage,
    currentPage,
    totalItems,
    perPageOptions,

    // Search
    searchQuery,

    // Data fetching
    fetchItems,
    refetchData,

    selectedRows,
    selectAll,
    toggleSelectAll,
  };
}
